import { useCallback, useEffect, useState } from 'react'
import {
  DevicesError,
  loadDevices,
  revokeDeviceFlow,
  type DevicesFlowDeps,
  type UiDevice,
} from '../services/devicesFlow'

/**
 * `pages/settings/TrustedDevicesSection.tsx` 用 state machine (Track E-3)。
 *
 * 状態:
 *   loading → ready (devices[]) | error (messageKey)
 *   revoke 成功時は ready を再取得 (caller が `refresh()` を呼ぶ)
 *
 * `messageKey` は CLAUDE.md §2 ルール 10 準拠で string を返し、UI 側で `t()` 当てる。
 */

const MESSAGE_KEYS: Record<DevicesError['code'], string> = {
  unauthorized: 'settings.security.trusted_devices_error.unauthorized',
  not_found: 'settings.security.trusted_devices_error.not_found',
  too_many_requests: 'settings.security.trusted_devices_error.too_many_requests',
  network: 'settings.security.trusted_devices_error.network',
  generic: 'settings.security.trusted_devices_error.generic',
}

const messageKeyFor = (err: unknown): string => {
  if (err instanceof DevicesError) return MESSAGE_KEYS[err.code]

  return MESSAGE_KEYS.generic
}

export type TrustedDevicesState =
  | { kind: 'loading' }
  | { kind: 'ready'; devices: UiDevice[] }
  | { kind: 'error'; messageKey: string }

export interface UseTrustedDevices {
  state: TrustedDevicesState
  refresh: () => Promise<void>
  /** revoke 成功で true、失敗で false を返す (UI が成功 Toast を出す判定に使う)。 */
  revoke: (sessionId: string, isCurrent: boolean) => Promise<boolean>
  /** 直前の revoke / refresh で起きたエラー (UI が banner で再表示する用、null = なし) */
  lastErrorKey: string | null
}

export interface UseTrustedDevicesOptions {
  deps?: DevicesFlowDeps
}

export const useTrustedDevices = (options: UseTrustedDevicesOptions = {}): UseTrustedDevices => {
  const [state, setState] = useState<TrustedDevicesState>({ kind: 'loading' })
  const [lastErrorKey, setLastErrorKey] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' })
    setLastErrorKey(null)
    try {
      const devices = await loadDevices(options.deps)

      setState({ kind: 'ready', devices })
    } catch (err: unknown) {
      const key = messageKeyFor(err)

      setState({ kind: 'error', messageKey: key })
      setLastErrorKey(key)
    }
  }, [options.deps])

  const revoke = useCallback(
    async (sessionId: string, isCurrent: boolean): Promise<boolean> => {
      setLastErrorKey(null)
      try {
        await revokeDeviceFlow(sessionId, { isCurrent }, options.deps)
      } catch (err: unknown) {
        setLastErrorKey(messageKeyFor(err))

        return false
      }

      if (isCurrent) {
        // 自身 revoke 後は sessionStore.clear() が走っているので一覧再取得は不要
        // (UI 側で #login へ navigate される)。state は ready のまま放置。
        return true
      }

      await refresh()

      return true
    },
    [options.deps, refresh],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  return { state, refresh, revoke, lastErrorKey }
}
